import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { Package, PackageInput, PackageUpdate, ProductList } from "@nh/shared";
import {
  archivePackage,
  createPackage,
  getAdminPackage,
  listAdminPackages,
  listProductsForPicker,
  updatePackage,
  type AdminPackageList,
  type AdminPackagesQuery,
} from "./packages.api";

/**
 * Las llaves de caché de los paquetes, en un solo sitio.
 *
 * Escribirlas a mano en cada hook es cómo se acaba con una mutación que
 * invalida `["packages"]` y una consulta guardada bajo
 * `["admin-packages"]`: todo compila, nada se refresca, y el paquete
 * recién creado no aparece hasta recargar la página.
 */
export const adminPackageKeys = {
  all: ["admin", "packages"] as const,
  list: (query: AdminPackagesQuery) => [...adminPackageKeys.all, "list", query] as const,
  one: (id: string) => [...adminPackageKeys.all, "one", id] as const,
  /** El catálogo que alimenta el selector de productos del formulario. */
  picker: ["admin", "packages", "product-picker"] as const,
};

export function useAdminPackages(
  query: AdminPackagesQuery,
): UseQueryResult<AdminPackageList, Error> {
  return useQuery({
    queryKey: adminPackageKeys.list(query),
    queryFn: () => listAdminPackages(query),
    /*
     * Al cambiar de página o de filtro, conservar la respuesta anterior
     * mientras llega la nueva: la tabla no se vacía ni salta a esqueleto
     * en cada cambio de filtro.
     */
    placeholderData: (previous) => previous,
  });
}

export function useAdminPackage(id: string | undefined): UseQueryResult<Package, Error> {
  return useQuery({
    queryKey: adminPackageKeys.one(id ?? ""),
    queryFn: () => getAdminPackage(id as string),
    enabled: Boolean(id),
  });
}

/**
 * El catálogo de productos para el selector y para el precio calculado.
 *
 * `staleTime` de cinco minutos: mientras se arma un paquete se consulta
 * en cada tecleo del descuento y en cada fila añadida, y el catálogo no
 * cambia entre una y otra. Sin esto, cada remontaje del formulario
 * dispara otra petición de cien productos.
 */
export function useProductPicker(): UseQueryResult<ProductList, Error> {
  return useQuery({
    queryKey: adminPackageKeys.picker,
    queryFn: () => listProductsForPicker(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreatePackage(): UseMutationResult<Package, Error, PackageInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PackageInput) => createPackage(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminPackageKeys.all });
    },
  });
}

export function useUpdatePackage(
  id: string,
): UseMutationResult<Package, Error, PackageUpdate> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: PackageUpdate) => updatePackage(id, patch),
    onSuccess: (pkg) => {
      queryClient.setQueryData(adminPackageKeys.one(id), pkg);
      void queryClient.invalidateQueries({ queryKey: adminPackageKeys.all });
    },
  });
}

export function useArchivePackage(): UseMutationResult<Package, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => archivePackage(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminPackageKeys.all });
    },
  });
}
